import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { SETTINGS_SERVICE, SettingsService } from 'ziti-console-lib';
import { ZitiDomainControllerService, ZitiSessionData } from 'ziti-console-lib';
import { delay, get, isEmpty } from 'lodash';
import { ApiService, MenuService } from '@netfoundry-ui/shared/services';
import moment from 'moment/moment';
import { Environment, ENVIRONMENT } from '@netfoundry-ui/shared/model';
import {NETWORK_SERVICE, NetworkServiceV2} from './network.service';
import {Router} from "@angular/router";
import {GrowlerData, GrowlerService} from "@netfoundry-ui/shared/growler";
import {ConfirmComponent} from "@netfoundry-ui/ui/confirm";
import {MatDialog} from "@angular/material/dialog";
import {URLS} from '@netfoundry-ui/shared/services';
import {HttpClient, HttpContext, HttpHeaders} from "@angular/common/http";
import { ZitiWasmService } from '@netfoundry-ui/shared/ziti-wasm';
import {Auth0Service} from '@netfoundry-ui/shared/authorization';

@Injectable({
    providedIn: 'root',
})
export class ZitiControllerService implements ZitiDomainControllerService {
    currentNetwork: any = {};
    tokenExpirationDate: any = {};
    dialogRef: any;

    nfAdminAuthPolicyId: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);

    public zitiSessionData: ZitiSessionData = {
        zitiDomain: '',
        zitiSessionId: '',
        expiresAt: '',
    };

    public zitiSettings: any = new BehaviorSubject(this.zitiSessionData);
    public loadingZitiController = false;
    // One pending refresh at most; each new session replaces it, or every mint adds a refresh that never stops
    private sessionRefreshTimer: any;

    constructor(
        @Inject(SETTINGS_SERVICE) private settingsService: SettingsService,
        private apiService: ApiService,
        @Inject(NETWORK_SERVICE) private networkServiceV2: NetworkServiceV2,
        @Inject(ENVIRONMENT) private environment: Environment,
        private router: Router,
        private growlService: GrowlerService,
        private menuService: MenuService,
        private dialogForm: MatDialog,
        private httpClient: HttpClient,
        private auth0Service: Auth0Service,
        private zitiWasmService: ZitiWasmService
    ) {
        if (!this.environment.v3Enabled) {
            return;
        }
        this.apiService.currentNetwork.subscribe(async (network) => {
            if (isEmpty(network?.id)) {
                return;
            }
            // Skip until authenticated. A network rehydrated from the stored cookie on reload
            // would otherwise fire a doomed connect and pop the controller modal on the login screen.
            if (!this.auth0Service?.authenticated) {
                return;
            }
            if (this.currentNetwork?.id !== network?.id) {
                this.currentNetwork = network;
                if (this.currentNetwork?.status === 'PROVISIONING' || this.currentNetwork?.status === 'NEW') {
                  this.router.navigate([URLS.PROCESS_EXECUTIONS]);
                  this.pollForNetworkProvisioned();
                } else if (this.currentNetwork?.status === 'PROVISIONED') {
                  this.getZitiControllerSession();
                } else {
                  this.router.navigate([URLS.PROCESS_EXECUTIONS]);
                }
            } else {
                this.currentNetwork = network;
            }
        });
    }

    getZitiControllerSession(retryOnFail = false, retryCount = 0) {
        // No network selected yet; an exchange for an undefined id 400s and pops the connect modal.
        if (isEmpty(this.currentNetwork?.id)) {
            return;
        }
        this.loadingZitiController = true;
        this.nfAdminAuthPolicyId.next(undefined);
        this.zitiSessionData = {
          zitiDomain: '',
          zitiSessionId: '',
          expiresAt: '',
        };
        const zitiSettings = { ...this.zitiSessionData };
        this.zitiSettings.next(zitiSettings);
        // Disconnect old WASM Ziti context before obtaining a new session
        this.zitiWasmService.disconnect().then(() => {
          this.networkServiceV2
            .getZitiSession(this.currentNetwork?.id)
            .toPromise()
            .then((result) => {
              this.updateZitiSession(result);
            }).catch((er) => this.handleExchangeError(er));
        });
    }

    /**
     * Renews the session before it expires. The exchange returns its cached token until that token is nearly expired;
     * the same token changes nothing, so it does not clear the session or restart the WASM overlay.
     */
    refreshZitiControllerSession() {
        // A mint in progress schedules its own refresh
        if (isEmpty(this.currentNetwork?.id) || this.loadingZitiController) {
            return;
        }
        const networkId = this.currentNetwork.id;
        this.networkServiceV2
          .getZitiSession(networkId)
          .toPromise()
          .then((result) => {
            if (networkId !== this.currentNetwork?.id) {
              return;
            }
            if (result?.value === this.zitiSessionData.zitiSessionId) {
              this.scheduleSessionRefresh(result.expiresAt);
            } else {
              this.updateZitiSession(result);
            }
          }).catch((er) => this.handleExchangeError(er));
    }

    isControllerUnreachable(er: any): boolean {
        return er?.status === 503;
    }

    handleControllerUnreachable() {
        this.loadingZitiController = false;
        const data = {
          title: 'Unable to Connect',
          appendId: 'ControllerUnavailbale',
          subtitle: "We couldn't connect to the controller. Please try again in a few minutes. If the issue persists, contact the support team for further assistance.",
          icon: 'Caution',
          action: 'Close',
          hideCancelLink: true,
        };
        this.dialogRef = this.dialogForm.open(ConfirmComponent, {
          data: data,
          height: '340px',
          width: '600px',
          autoFocus: false,
        });
    }

    // core-management returns a 403 with this discriminator when read-only (non-admin) access is attempted against a
    // network whose Ziti version is too old to support read-only access. The `type` is top-level; `code` is nested under `properties`.
    isZitiVersionUpgradeRequired(er: any): boolean {
        const errorBody = er?.error;
        return errorBody?.type === 'urn:netfoundry:ziti-version-upgrade-required'
            || errorBody?.properties?.code === 'ZITI_VERSION_UPGRADE_REQUIRED';
    }

    handleZitiVersionUpgradeRequired(errorBody: any) {
        this.loadingZitiController = false;
        const currentZitiVersion = errorBody?.properties?.currentZitiVersion;
        const subtitle = currentZitiVersion
            ? `This network is running Ziti ${currentZitiVersion}. Upgrade the network to 2.0.0 or later to get read-only access.`
            : 'Upgrade the network to 2.0.0 or later to get read-only access.';
        const data = {
            title: 'Upgrade Required',
            appendId: 'ZitiVersionUpgradeRequired',
            subtitle: subtitle,
            icon: 'Confirm',
            action: 'Close',
            hideCancelLink: true,
        };
        this.dialogRef = this.dialogForm.open(ConfirmComponent, {
            data: data,
            height: '340px',
            width: '600px',
            autoFocus: false,
        });
    }

    updateZitiSession(exchangeResult: any) {
        const url = new URL(exchangeResult?.networkControllerUrl);
        const edgeUrl = `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`;
        const settings = {
            ...this.settingsService.settings,
            session: {
                type: exchangeResult.type,
                id: exchangeResult.value,
                controllerDomain: edgeUrl,
                authorization: 100,
                expiresAt: exchangeResult.expiresAt,
            },
        };
        settings.selectedEdgeController = edgeUrl
        this.zitiSessionData.zitiSessionId = exchangeResult.value;
        this.zitiSessionData.zitiDomain = edgeUrl;
        this.zitiSessionData.expiresAt = exchangeResult.expiresAt;
        this.setApiVersions(edgeUrl);
        this.settingsService.set(settings);
        this.settingsService.zitiSemver = exchangeResult.zitiVersion;
        this.tokenExpirationDate = moment(exchangeResult.expiresAt);
        this.scheduleSessionRefresh(exchangeResult.expiresAt);
        const zitiSettings = { ...this.zitiSessionData };
        this.zitiSettings.next(zitiSettings);
        this.apiService.setCurrentZitiEdgeDetails(exchangeResult);
        this.loadingZitiController = false;
        this.getNFManagedAuthPolicyId(edgeUrl, exchangeResult.type, exchangeResult.value);
    }

    setApiVersions(edgeUrl) {
      const defaultAPIVersions: any = {
        "edge": {
          "v1": {
            "apiBaseUrls": [
              `${edgeUrl}/edge/client/v1`
            ],
            "path": "/edge/client/v1"
          }
        },
        "edge-client": {
          "v1": {
            "apiBaseUrls": [
              `${edgeUrl}/edge/client/v1`
            ],
            "path": "/edge/client/v1"
          }
        },
        "edge-management": {
          "v1": {
            "apiBaseUrls": [
              `${edgeUrl}/edge/management/v1`
            ],
            "path": "/edge/management/v1"
          }
        }
      };
      this.settingsService.apiVersions = defaultAPIVersions;
    }

    get hasZitiSession() {
      const hasSessionId = !isEmpty(this.zitiSessionData?.zitiSessionId);
      const sessionExpired = moment(this.zitiSessionData?.expiresAt).isBefore(new Date());
      return hasSessionId && !sessionExpired;
    }

    handleControllerSessionError() {
      this.loadingZitiController = false;
      this.growlService.show(
          new GrowlerData(
            'error',
            'Error',
            'Failed to connect',
            'Can not establish a session with network controller'
          )
      );
      if (this.menuService.areaId === 'ZAC') {
          const data = {
              title: 'Unable to Connect',
              appendId: 'ZitiConnectionFailed',
              subtitle: 'Unable to connect to the ziti controller for the selected network. Would you like to try again?',
              icon: 'Confirm',
              action: 'Yes',
          };
          this.dialogRef = this.dialogForm.open(ConfirmComponent, {
              data: data,
              height: '340px',
              width: '600px',
              autoFocus: false,
          });
          this.dialogRef.afterClosed().subscribe((result: any) => {
              if (result) {
                  this.getZitiControllerSession();
              } else {
                  this.router.navigate(['/infrastructure/dashboard']);
              }
          });
      } else {
              const data = {
              title: 'Unable to Connect',
              appendId: 'ZitiConnectionFailed',
              subtitle: 'Unable to connect to the ziti controller for the selected network.',
              icon: 'Confirm',
              action: 'Restore',
              isRestoreModal: true,
              networkId: this.currentNetwork.id
          };
          this.dialogRef = this.dialogForm.open(ConfirmComponent, {
              data: data,
              height: '340px',
              width: '600px',
              autoFocus: false,
          });
          this.dialogRef.afterClosed().subscribe((result: any) => {
              if (result) {
                  this.networkServiceV2.restoreNetwork(this.currentNetwork.id).subscribe((data) => {
                this.growlService.show(
                new GrowlerData('success', 'Success', `Restore process was started successfully`)
            );
                  });
              } else {
                  this.router.navigate(['/network/dashboard']);
              }
          });
      }
    }

    pollForNetworkProvisioned() {
      this.loadingZitiController = true;
      const options = {
        params: {
          embed: 'clusters,network-controllers'
        }
      };
      this.networkServiceV2.getNetwork(this.currentNetwork.id, options).then((network: any) => {
        this.currentNetwork = this.apiService.getNetworkModel(network);
        if (this.currentNetwork.status === 'PROVISIONED') {
          this.loadingZitiController = false;
          this.apiService.setCurrentNetwork(this.currentNetwork);
          this.getZitiControllerSession();
        } else if (this.currentNetwork.status === 'PROVISIONING' || this.currentNetwork.status === 'NEW') {
          delay(() => {
            this.pollForNetworkProvisioned();
          }, 15000);
        } else if (this.currentNetwork.status === 'ERROR') {
          this.loadingZitiController = false;
          this.growlService.show(
            new GrowlerData(
              'error',
              'Error',
              'Failed to connect',
              'Cannot connect to ziti controller. Network failed to provision.'
            )
          );
        }
      }).catch(() => {
        this.loadingZitiController = false;
      });
    }

    getNFManagedAuthPolicyId(edgeUrl: string, sessionType: string, token: string): Promise<any> {
        const url = edgeUrl + `/edge/management/v1/auth-policies?filter= name = "NetFoundry Console Integration Auth Policy"`;
        let headers = new HttpHeaders();
        if(sessionType === 'SESSION') {
          headers = headers.set('zt-session', token);
        } else {
          headers = headers.set('Authorization', `Bearer ${token}`);
        }
        return this.httpClient.get(url, {
          headers: headers
        }).toPromise().then((result: any) => {
          const authPolicies = result?.data || [];
          const nfPolicy = authPolicies[0];
          this.settingsService.settings.nfAdminAuthPolicyId = nfPolicy?.id;
          this.nfAdminAuthPolicyId.next(nfPolicy?.id);
        }).catch(() => {
          // set an empty auth policy, so that any queued requests for entities can continue
          this.nfAdminAuthPolicyId.next('EMPTY');
        });
    }

    handleUnauthorized(): any {
      // No-Op
    }

    private handleExchangeError(er: any) {
        if (this.isZitiVersionUpgradeRequired(er)) {
          this.handleZitiVersionUpgradeRequired(er?.error);
        } else if (this.isControllerUnreachable(er)) {
          this.handleControllerUnreachable()
        } else {
          this.handleControllerSessionError();
        }
    }

    private scheduleSessionRefresh(expiresAt: string) {
        const expTime = moment(expiresAt).diff(moment());
        let buffer = expTime - 1000 * 30;
        if (buffer < 10000) {
            buffer = 10000;
        }
        clearTimeout(this.sessionRefreshTimer);
        this.sessionRefreshTimer = delay(() => this.refreshZitiControllerSession(), buffer);
    }
}
